/**
 * /timeline-maker — a timeline on a real time scale, the page.
 *
 * The table is the document (date · event · description · category · picture);
 * the sheet above it is DRAWN from it on every change — one scene (layout.js)
 * read by the SVG on screen, the canvas for PNG and pdf-lib for PDF, so what is
 * printed is what was seen.
 *
 * Work is kept through the shared draft store (tool-draft.js over
 * work-storage.js): every change is scheduled, pictures travel as bytes in the
 * same record, a reload reopens the timeline that was open. localStorage is
 * NOT used for the work (Chromium commits it about once a minute — a browser
 * crash loses the last edits; measured 29.09.2026).
 */
import { parseDate, formatDate, formatYear, formatAgo, centuryOf, hist, dateOfT, tOf, daysInMonth, isBC, nfmt, rangeJoin, formatEnds } from './dates.js';
import { rules } from './notation.js';
import { fill, iso as isoDir, ltr as ltrDir, listOf, textDir } from './i18n.js';
import { snapUnit } from './scale.js';
import { layout, NARROW_MIN } from './layout.js';
import { toSVG } from './svg.js';
import { drawScene, toPNG, fitDensity } from './canvas.js';
import { adopt, analyze, emptyDoc, newEvent, newId, plain, History, colorOf, yearRange, LIMITS } from './model.js';
import { theme, faces, THEMES } from './themes.js';
import { parsePaste, toCSV } from './paste.js';
import { encode, decode, payloadFromHash, SAFE_LINK_CHARS, projectJSON, readProject } from './share.js';
import * as TM from './telemetry.js';

const CFG = window.__TLM_CFG || {};
const I18N = CFG.i18n || {};
const TPL = CFG.templates || {};
const SLUG = 'timeline-maker';
const LOCALE = String(window.__LOCALE || document.documentElement.lang || 'en').slice(0, 2);
const $ = (id) => document.getElementById(id);
const t = (k) => (I18N[k] != null ? I18N[k] : k);
const toast = (m, type) => { try { if (window.__toast) window.__toast(m, type || 'info'); } catch (_) { /* no toast layer */ } };
/* A right-to-left page (ar — or any page a tester switches with dir="rtl"): the
   dates, periods and formulas the code builds are set apart inside its sentences. */
const rtlPage = () => String(document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
const iso = (s) => isoDir(s, rtlPage());
const ltr = (s) => ltrDir(s, rtlPage());
const quoted = (s) => fill(t('quoted'), { s: iso(s) });

/* ── state ───────────────────────────────────────────────────────────────── */

const S = {
    doc: null,
    images: new Map(),      // id → Blob (JPEG/PNG, already shrunk)
    bitmaps: new Map(),     // id → ImageBitmap (PNG/PPTX drawing)
    urls: new Map(),        // id → object URL (SVG on screen)
    sizes: new Map(),       // id → {w,h}
    selected: null,
    an: null, out: null,
    hist: new History(160),
    draftId: null,
    fontsReady: false,
    renderQueued: false,
    rowsDirty: true,
    lastRender: 0,
};
window.__tlmState = S;      // the browser guard reads the live state through this

/* ── fonts: the same TTF on screen, in PNG and in PDF ────────────────────── */

const PTF = () => window.PdfTextFont || null;
const measureCanvas = document.createElement('canvas').getContext('2d');
const measureCache = new Map();
let FAM = { title: 'sans-serif', body: 'sans-serif', titleBold: 'sans-serif', bodyBold: 'sans-serif' };

function cssFam(id, bold) {
    const p = PTF();
    if (!p || !p.cssFamily) return null;
    try { return p.cssFamily(id, bold); } catch (_) { return null; }
}
function stack(id, bold) {
    const out = [];
    const main = cssFam(id, bold);
    if (main) out.push('"' + main + '"');
    const noto = id !== 'noto' ? cssFam('noto', bold) : null;
    if (noto) out.push('"' + noto + '"');
    const ar = cssFam('arabic', bold);
    if (ar) out.push('"' + ar + '"');
    out.push('system-ui', 'sans-serif');
    return out.join(', ');
}
let fontsFor = '';
async function ensureFonts() {
    const fc = faces(S.doc.view);
    const key = fc.title + '|' + fc.body;
    if (key === fontsFor && S.fontsReady) return;
    fontsFor = key;
    const p = PTF();
    if (!p) {
        // the font helper did not arrive (both addresses): the sheet is drawn in the system face
        if (!S.fontsMissing) { S.fontsMissing = true; TM.critical('tlm_asset_failed', { asset: 'pdf-text-font' }); }
        S.fontsReady = true;
        return;
    }
    try { await p.load(); } catch (_) { TM.critical('tlm_asset_failed', { asset: 'fonts' }); /* coverage table optional: system fonts then */ }
    FAM = { title: stack(fc.title, false), titleBold: stack(fc.title, true), body: stack(fc.body, false), bodyBold: stack(fc.body, true) };
    measureCache.clear();
    const loads = [];
    for (const [id, bold] of [[fc.title, true], [fc.title, false], [fc.body, true], [fc.body, false], ['noto', true], ['noto', false]]) {
        const fam = cssFam(id, bold);
        if (fam && document.fonts && document.fonts.load) loads.push(document.fonts.load((bold ? 700 : 400) + ' 16px "' + fam + '"').catch(() => null));
    }
    await Promise.all(loads);
    if (fontsFor !== key) return;
    measureCache.clear();
    S.fontsReady = true;
    queueRender();
}
/* The stand lays the examples out with the page's own text metrics (the real
   fonts of each example's theme): the no-crossing guard must hold for these
   widths, not only for a model. */
window.__tlmMeasureFor = async (view) => {
    const fc = faces(view);
    const fam = { title: stack(fc.title, false), titleBold: stack(fc.title, true), body: stack(fc.body, false), bodyBold: stack(fc.body, true) };
    const loads = [];
    for (const [id, bold] of [[fc.title, true], [fc.title, false], [fc.body, true], [fc.body, false]]) {
        const f = cssFam(id, bold);
        if (f && document.fonts && document.fonts.load) loads.push(document.fonts.load((bold ? 700 : 400) + ' 16px "' + f + '"').catch(() => null));
    }
    await Promise.all(loads);
    const g = document.createElement('canvas').getContext('2d');
    return (text, size, weight, role) => {
        const bold = (weight || 400) >= 600;
        g.font = (bold ? 700 : 400) + ' ' + size + 'px ' + (role === 'title' ? (bold ? fam.titleBold : fam.title) : (bold ? fam.bodyBold : fam.body));
        return g.measureText(String(text)).width;
    };
};
window.__tlmLabels = () => ({ approx: t('legend_approx'), gap: t('legend_gap') });
function measure(text, size, weight, role) {
    const bold = (weight || 400) >= 600;
    const fam = role === 'title' ? (bold ? FAM.titleBold : FAM.title) : (bold ? FAM.bodyBold : FAM.body);
    const key = fam + '|' + size + '|' + (bold ? 7 : 4) + '|' + text;
    let w = measureCache.get(key);
    if (w == null) {
        measureCanvas.font = (bold ? 700 : 400) + ' ' + size + 'px ' + fam;
        w = measureCanvas.measureText(String(text)).width;
        if (measureCache.size > 20000) measureCache.clear();
        measureCache.set(key, w);
    }
    return w;
}

/* ── telemetry (class, count and length only — never the text) ──────────── */

/* the instruments live in telemetry.js; here — what the page knows: the SHAPE of the timeline */
const stage = TM.stageOnce;
const ae = TM.ae;
const funnel = TM.funnel;
function docShape() {
    const an = S.an || analyze(S.doc, { locale: LOCALE });
    const v = S.doc.view;
    return {
        events: an.items.length, periods: an.items.filter((i) => i.period).length, errors: Object.keys(an.errors || {}).length,
        mode: an.mode, bc: an.hasBC ? 1 : 0, cats: S.doc.cats.length, images: usedImageIds().length,
        layout: v.layout, scale: v.scale, theme: v.theme, dir: v.dir, locale: LOCALE,
    };
}
/** The person got a file: once per (way, this very timeline). */
function complete(via, extra) {
    const key = via + ':' + TM.hashOf(JSON.stringify(plain(S.doc)) + '|' + usedImageIds().join(','));
    TM.complete(key, Object.assign({ via }, docShape(), extra || {}));
}

/* ── documents: change, history, draft ───────────────────────────────────── */

function commit(key, opts) {
    S.hist.push(S.doc, key, 1200);
    updateUndo();
    if (opts && opts.rows) S.rowsDirty = true;
    queueRender();
    draftTouch();
    scheduleLink();
}
function updateUndo() {
    $('tlmUndo').disabled = !S.hist.canUndo();
    $('tlmRedo').disabled = !S.hist.canRedo();
}
function undo() {
    const d = S.hist.undo();
    if (!d) return;
    S.doc = adopt(d, LOCALE);
    afterDocSwap();
    ae('undo');
}
function redo() {
    const d = S.hist.redo();
    if (!d) return;
    S.doc = adopt(d, LOCALE);
    afterDocSwap();
    ae('redo');
}
function afterDocSwap() {
    updateUndo();
    S.rowsDirty = true;
    syncControls();
    ensureFonts();
    queueRender();
    draftTouch();
    scheduleLink();
}

function openDoc(doc, images, opts) {
    S.doc = adopt(doc, LOCALE);
    for (const u of S.urls.values()) URL.revokeObjectURL(u);
    S.images = new Map(); S.bitmaps = new Map(); S.urls = new Map(); S.sizes = new Map();
    const loads = [];
    if (images) for (const [id, blob] of images) loads.push(addImageBlob(id, blob));
    S.selected = null;
    S.hist.reset(S.doc);
    S.rowsDirty = true;
    showEmpty(false);
    syncControls();
    updateUndo();
    ensureFonts();
    Promise.all(loads).then(() => queueRender());
    queueRender();
    if (opts && opts.draftId) S.draftId = opts.draftId;
    linkCache = null;
    scheduleLink();
}

/* A phone-wide sheet reads better top to bottom: a NEW timeline (an example or a
   blank one) starts vertical there. A saved one keeps the layout it was given. */
function narrowStart(doc) {
    const w = ($('tlmSheet') && $('tlmSheet').clientWidth) || window.innerWidth || 1000;
    if (w < 560) doc.view.layout = 'vertical';
    return doc;
}

function templateDoc(key) {
    const tp = TPL[key];
    if (!tp) return null;
    return narrowStart({
        title: tp.title, author: '',
        cats: (tp.cats || []).map((c) => ({ id: c.id, name: c.name, color: c.color })),
        events: (tp.events || []).map((e) => newEvent({ date: e.date, title: e.title, desc: e.desc || '', cat: e.cat || null })),
        view: Object.assign({}, emptyDoc(LOCALE).view, tp.view || {}),
    });
}

/* ── the draft store ─────────────────────────────────────────────────────── */

const DRAFT_AGE = 365 * 864e5;
let STORE = null;
function store() {
    if (STORE !== null) return STORE;
    STORE = false;
    try {
        // No count cap (was 40): a timeline is a few kilobytes (18.10.2026, report #9299).
        if (window.__toolDraft) STORE = window.__toolDraft.store(SLUG, { maxAgeMs: DRAFT_AGE, debounceMs: 500, onConflict: draftForked });
    } catch (_) { STORE = false; }
    return STORE;
}
function newDraftId() { return 'tl' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
function draftTouch() {
    const st = store();
    if (!st || !S.doc) return;
    if (!S.draftId) { S.draftId = newDraftId(); st.claim(S.draftId); }
    setSave('saving');
    st.schedule(S.draftId, draftPayload, draftSaved);
}
function usedImageIds() {
    const ids = [];
    for (const e of S.doc.events) if (e.img && S.images.has(e.img) && ids.indexOf(e.img) < 0) ids.push(e.img);
    return ids;
}
function draftPayload() {
    const ids = usedImageIds();
    return {
        v: 1,
        state: { doc: plain(S.doc), imgs: ids },
        files: ids.map((id) => S.images.get(id)),
        label: docLabel(S.doc),
        preview: draftPreview(),
        meta: { n: S.doc.events.length, editedAt: Date.now() },
    };
}
function docLabel(d) {
    const tt = String(d.title || '').trim();
    if (tt) return tt;
    const first = d.events.find((e) => String(e.title || '').trim());
    return first ? String(first.title).trim() : '';
}
let previewAt = 0, previewUrl = '';
function draftPreview() {
    const now = Date.now();
    if (previewUrl && now - previewAt < 3000) return previewUrl;
    try {
        if (!S.out || !S.fontsReady) return previewUrl;
        const sc = S.out.scene;
        const k = Math.min(240 / sc.w, 150 / sc.h);
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(sc.w * k)); c.height = Math.max(1, Math.round(sc.h * k));
        drawScene(c.getContext('2d'), sc, { density: k, family: FAM, images: S.bitmaps });
        previewUrl = c.toDataURL('image/jpeg', 0.7);
        previewAt = now;
    } catch (_) { /* a row without a picture still opens */ }
    return previewUrl;
}
function draftSaved(r) {
    if (!r) return;
    if (r.ok) { setSave('saved'); refreshDraftList(); return; }
    if (r.why === 'foreign') return;
    setSave('failed');
    ae('draft_save_failed', { why: String(r.why || '').slice(0, 30) });
}
function draftForked(id) {
    if (id !== S.draftId) return;
    const st = store(); if (!st) return;
    S.draftId = newDraftId();
    st.claim(S.draftId);
    st.schedule(S.draftId, draftPayload, draftSaved);
    toast(t('draft_forked'), 'info');
    ae('draft_forked');
}
function setSave(state) {
    const el = $('tlmSaveState');
    if (!el) return;
    el.classList.toggle('bad', state === 'failed');
    el.textContent = state === 'saved' ? '✓ ' + t('save_saved') : state === 'failed' ? t('save_failed') : '';
}

async function loadDraft(id) {
    const st = store(); if (!st) return false;
    const rec = await st.load(id);
    if (!rec || !rec.state || !rec.state.doc) return false;
    const imgs = new Map();
    (rec.state.imgs || []).forEach((iid, i) => { if (rec.files && rec.files[i]) imgs.set(iid, rec.files[i]); });
    if (S.draftId && S.draftId !== id) st.release(S.draftId);
    const ok = await st.claim(id);
    S.draftId = ok ? id : newDraftId();
    if (!ok) st.claim(S.draftId);
    openDoc(rec.state.doc, imgs);
    if (!ok) { toast(t('draft_forked'), 'info'); draftTouch(); }
    refreshDraftList();
    return true;
}

let draftRows = [];
async function refreshDraftList() {
    const st = store();
    const box = $('tlmDrafts');
    if (!st || !box) return;
    draftRows = await st.list();
    $('tlmDraftsN').textContent = draftRows.length ? '· ' + draftRows.length : '';
    box.textContent = '';
    if (!draftRows.length) {
        const p = document.createElement('p'); p.className = 'tc-hint'; p.textContent = t('drafts_empty'); box.appendChild(p);
        return;
    }
    for (const r of draftRows) {
        const row = document.createElement('div');
        row.className = 'tlm-draft' + (r.id === S.draftId ? ' cur' : '');
        let pic;
        if (r.preview) { pic = document.createElement('img'); pic.src = r.preview; pic.alt = ''; } else { pic = document.createElement('span'); pic.className = 'ph'; }
        const info = document.createElement('div');
        const tt = document.createElement('p'); tt.className = 'tlm-draft-t'; tt.dir = 'auto'; tt.textContent = r.label || t('draft_untitled');
        const mm = document.createElement('p'); mm.className = 'tlm-draft-m';
        const evN = (r.meta && r.meta.n) || 0;
        mm.textContent = [fmtWhen(r.savedAt), fill(t('drafts_events'), { n: evN }), r.id === S.draftId ? t('drafts_current') : ''].filter(Boolean).join(' · ');
        info.append(tt, mm);
        const acts = document.createElement('div'); acts.className = 'tlm-draft-a';
        const btn = (label, fn, cls) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'tc-btn sm ' + (cls || 'ghost'); b.textContent = label; b.addEventListener('click', fn); acts.appendChild(b); return b; };
        if (r.id !== S.draftId) btn(t('drafts_open'), () => { loadDraft(r.id); ae('draft_open'); }, '');
        btn(t('drafts_dup'), () => duplicateDraft(r.id));
        const del = btn(t('drafts_delete'), () => {
            // the question is a phrase of its own: «Supprimer ?» in French, «¿Borrar?» in Spanish
            if (!del.dataset.armed) { del.dataset.armed = '1'; del.textContent = t('drafts_delete_confirm'); setTimeout(() => { if (del.isConnected) { delete del.dataset.armed; del.textContent = t('drafts_delete'); } }, 3500); return; }
            deleteDraft(r.id, r.label);
        });
        row.append(pic, info, acts);
        box.appendChild(row);
    }
}
function fmtWhen(ts) {
    // the page's Intl tag (Arabic with Latin digits, as the sheet prints them) — not the DOM's lang
    const loc = window.__intlLocale ? window.__intlLocale() : LOCALE;
    try { return new Date(ts).toLocaleString(loc, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; }
}
async function duplicateDraft(id) {
    const st = store(); if (!st) return;
    let doc, imgs;
    if (id === S.draftId) { doc = plain(S.doc); imgs = new Map(S.images); }
    else {
        const rec = await st.load(id);
        if (!rec) return;
        doc = rec.state.doc; imgs = new Map();
        (rec.state.imgs || []).forEach((iid, i) => { if (rec.files && rec.files[i]) imgs.set(iid, rec.files[i]); });
    }
    const copyOf = docLabel(doc) || t('draft_untitled');
    doc.title = fill(t('draft_copy'), { title: copyOf });
    if (S.draftId) st.release(S.draftId);
    S.draftId = newDraftId();
    st.claim(S.draftId);
    openDoc(doc, imgs);
    draftTouch();
    ae('draft_dup');
}
async function deleteDraft(id, label) {
    const st = store(); if (!st) return;
    // a save still scheduled for this timeline would write it back right after the removal
    if (id === S.draftId && st.cancelScheduled) st.cancelScheduled();
    await st.remove(id);
    const gone = iso(label || t('draft_untitled'));
    toast(fill(t('drafts_deleted'), { title: gone }), 'info');
    ae('draft_delete');
    if (id === S.draftId) {
        S.draftId = null;
        S.doc = emptyDoc(LOCALE);
        S.hist.reset(S.doc);
        S.rowsDirty = true;
        showEmpty(true);
        queueRender();
    }
    refreshDraftList();
}

/* ── pictures ────────────────────────────────────────────────────────────── */

const IMG_MAX = 900;
async function shrinkImage(file) {
    let bmp;
    try { bmp = await createImageBitmap(file); } catch (_) {
        bmp = await new Promise((res, rej) => {
            const im = new Image();
            im.onload = () => res(im); im.onerror = () => rej(new Error('img'));
            im.src = URL.createObjectURL(file);
        });
    }
    const w = bmp.width, h = bmp.height;
    if (!(w > 0 && h > 0)) throw new Error('img');
    const k = Math.min(1, IMG_MAX / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
    const g = c.getContext('2d');
    const png = /png|gif/i.test(file.type || '');
    if (!png) { g.fillStyle = '#ffffff'; g.fillRect(0, 0, c.width, c.height); }
    g.drawImage(bmp, 0, 0, c.width, c.height);
    // the site's one encoder (timbrica-core.js): toBlob → OffscreenCanvas → data URL, or a rejection (CANVAS_ENCODE_FAILED)
    return window.__canvasBlob(c, png ? 'image/png' : 'image/jpeg', 0.86);
}
async function addImageBlob(id, blob) {
    S.images.set(id, blob);
    if (S.urls.has(id)) URL.revokeObjectURL(S.urls.get(id));
    S.urls.set(id, URL.createObjectURL(blob));
    try {
        const bmp = await createImageBitmap(blob);
        S.bitmaps.set(id, bmp);
        S.sizes.set(id, { w: bmp.width, h: bmp.height });
    } catch (_) { S.sizes.delete(id); }
}

/* ── rendering ───────────────────────────────────────────────────────────── */

function queueRender() {
    if (S.renderQueued) return;
    S.renderQueued = true;
    requestAnimationFrame(() => { S.renderQueued = false; render(); });
}

function viewWidth() {
    const v = $('tlmView');
    const w = v && v.clientWidth ? v.clientWidth : ($('tlmSheet').clientWidth - 20);
    return Math.max(NARROW_MIN, w - 2);
}

function render() {
    if (!S.doc) return;
    const doc = S.doc;
    S.an = analyze(doc, { locale: LOCALE });
    if (S.rowsDirty) { buildRows(); S.rowsDirty = false; }
    updateFeedback();
    const vis = $('tlmView');
    updateScaleButtons();
    const opts = {
        measure, locale: LOCALE, width: viewWidth(), selected: S.selected,
        imageSize: (id) => S.sizes.get(id) || null,
        labels: { approx: t('legend_approx'), gap: t('legend_gap') },
        monthLabel: (y, m) => monthShort(y, m), dayLabel: (y, m, d) => dayShort(y, m, d),
    };
    S.out = layout(doc, S.an, opts);
    const svg = toSVG(S.out.scene, {
        family: FAM, interactive: true, title: doc.title || t('sheet_label'),
        imageUrl: (id) => S.urls.get(id) || null,
    });
    $('tlmHost').innerHTML = svg;
    vis.classList.toggle('vert', doc.view.layout === 'vertical');
    drawMini();
    updateStats();
    updateBetween();
    if (!$('tlmShow').hidden) showRender();
    S.lastRender = Date.now();
    window.__tlmRendered = (window.__tlmRendered || 0) + 1;
}

const MONTHS_SHORT = {};
function monthShort(y, m) {
    try {
        if (!MONTHS_SHORT[LOCALE]) MONTHS_SHORT[LOCALE] = new Intl.DateTimeFormat(LOCALE === 'ar' ? 'ar-u-nu-latn' : LOCALE, { month: 'short', timeZone: 'UTC' });
        return MONTHS_SHORT[LOCALE].format(new Date(Date.UTC(2001, m - 1, 15)));
    } catch (_) { return String(m); }
}
/* «1 oct.» is «1er oct.» in French: the first of the month is an ordinal there. */
function dayShort(y, m, d) { return d === 1 ? (LOCALE === 'fr' ? '1er' : '1') + ' ' + monthShort(y, m) : String(d); }

function pluralKey(prefix, n) {
    let cat = 'other';
    try { cat = new Intl.PluralRules(LOCALE).select(n); } catch (_) { /* default */ }
    const k = prefix + '_' + cat;
    return I18N[k] != null ? k : prefix + '_other';
}
function yearsText(n) { const num = nf(n); return fill(t(pluralKey('years', n)), { n: num }); }
function nf(n) { return nfmt(n, 0, LOCALE); }
/** A number a person reads with one decimal: «1,5×» in German, «7,3 pt» in French. */
function nf1(x) { return nfmt(x, 1, LOCALE); }

function updateStats() {
    const an = S.an;
    const el = $('tlmStats');
    if (!an.items.length) { el.textContent = t('stats_none'); return; }
    const periods = an.items.filter((i) => i.period).length;
    let span;
    const t0 = Math.min(...an.items.map((i) => i.a));
    const t1 = Math.max(...an.items.map((i) => (i.b == null ? i.a : i.b)));
    // the range the way the language joins it (ko «…~…»)
    if (an.mode === 'ago') span = iso(rangeJoin(formatAgo(Math.round(-t0), LOCALE), t1 >= -1 ? rules(LOCALE).present : formatAgo(Math.round(-t1), LOCALE), LOCALE));
    else {
        const { ya, yb } = yearRange(an.items);
        span = iso(rangeJoin(formatYear(ya, LOCALE, an.hasBC), formatYear(yb, LOCALE, an.hasBC), LOCALE)) + (yb > ya ? ' (' + iso(yearsText(yb - ya)) + ')' : '');
    }
    const n = an.items.length;
    el.textContent = fill(t('stats'), { n, p: periods, span });
}

/* ── the event table ─────────────────────────────────────────────────────── */

const rowEls = new Map();
function buildRows() {
    const box = $('tlmRows');
    const focusId = document.activeElement && document.activeElement.closest && document.activeElement.closest('.tlm-row') ? document.activeElement.closest('.tlm-row').dataset.id : null;
    const focusField = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.f : null;
    box.textContent = '';
    rowEls.clear();
    for (const ev of S.doc.events) box.appendChild(makeRow(ev));
    // a textarea measures its content only once it is in the document
    for (const ta of box.querySelectorAll('textarea')) autoGrow(ta);
    $('tlmRowsEmpty').hidden = S.doc.events.length > 0;
    $('tlmColHead').hidden = !S.doc.events.length;
    if (focusId && focusField && rowEls.has(focusId)) {
        const inp = rowEls.get(focusId).querySelector('[data-f="' + focusField + '"]');
        if (inp) inp.focus({ preventScroll: true });
    }
}

function catOptions(sel, current) {
    sel.textContent = '';
    const o0 = document.createElement('option'); o0.value = ''; o0.textContent = t('cat_none'); sel.appendChild(o0);
    for (const c of S.doc.cats) {
        const o = document.createElement('option'); o.value = c.id; o.textContent = c.name || '—'; sel.appendChild(o);
    }
    sel.value = current || '';
}

function makeRow(ev) {
    const row = document.createElement('div');
    row.className = 'tlm-row' + (S.selected === ev.id ? ' sel' : '');
    row.setAttribute('role', 'listitem');
    row.dataset.id = ev.id;
    const strip = document.createElement('span'); strip.className = 'tlm-row-strip'; strip.setAttribute('aria-hidden', 'true');
    const mk = (cls, el) => { const c = document.createElement('div'); c.className = 'tlm-cell ' + cls; c.appendChild(el); return c; };
    const date = document.createElement('input');
    date.type = 'text'; date.className = 'tlm-in'; date.dataset.f = 'date'; date.value = ev.date;
    date.placeholder = t('ph_date'); date.maxLength = LIMITS.date; date.setAttribute('aria-label', t('col_date'));
    date.autocomplete = 'off'; date.spellcheck = false; date.enterKeyHint = 'next';
    // iOS «corrects» «до н. э.» and capitalises «ок.»: a date is not prose
    date.setAttribute('autocorrect', 'off'); date.setAttribute('autocapitalize', 'off');
    // a pupil's own text: «753 ق.م» reads right to left, «753 BC» left to right, on any page
    date.dir = 'auto';
    const fb = document.createElement('div'); fb.className = 'tlm-fb'; fb.id = 'tlmFb_' + ev.id; fb.setAttribute('aria-live', 'polite');
    fb.dir = 'auto';   // a sentence of the page's language with a date in it: it reads the way its first letter does
    date.setAttribute('aria-describedby', fb.id);
    const cDate = mk('c-date', date);
    fb.classList.add('c-fb');
    const title = document.createElement('input');
    title.type = 'text'; title.className = 'tlm-in'; title.dataset.f = 'title'; title.value = ev.title;
    title.placeholder = t('ph_title'); title.maxLength = LIMITS.evTitle; title.setAttribute('aria-label', t('col_title')); title.enterKeyHint = 'next';
    title.dir = 'auto';
    const desc = document.createElement('textarea');
    desc.className = 'tlm-in'; desc.dataset.f = 'desc'; desc.value = ev.desc; desc.rows = 1;
    desc.placeholder = t('ph_desc'); desc.maxLength = LIMITS.desc; desc.setAttribute('aria-label', t('col_desc')); desc.enterKeyHint = 'next';
    desc.dir = 'auto';
    const cat = document.createElement('select');
    cat.className = 'tlm-in'; cat.dataset.f = 'cat'; cat.setAttribute('aria-label', t('col_cat'));
    catOptions(cat, ev.cat);
    const acts = document.createElement('div'); acts.className = 'tlm-row-act';
    const imgB = document.createElement('button'); imgB.type = 'button'; imgB.className = 'tlm-ib'; imgB.dataset.act = 'img';
    imgB.setAttribute('aria-label', t('row_image')); imgB.title = t('row_image');
    const thumb = document.createElement('img'); thumb.className = 'tlm-thumb'; thumb.alt = '';
    if (ev.img && S.urls.has(ev.img)) thumb.src = S.urls.get(ev.img); else thumb.hidden = true;
    imgB.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m21 16-5-5-8 8"/></svg>';
    if (!thumb.hidden) { imgB.textContent = ''; imgB.appendChild(thumb); }
    const del = document.createElement('button'); del.type = 'button'; del.className = 'tlm-ib'; del.dataset.act = 'del';
    del.setAttribute('aria-label', t('row_delete')); del.title = t('row_delete'); del.textContent = '✕';
    acts.append(imgB, del);
    acts.classList.add('c-act');
    row.append(strip, cDate, mk('c-title', title), mk('c-cat', cat), acts, fb, mk('c-desc', desc));
    rowEls.set(ev.id, row);
    return row;
}

function autoGrow(ta) {
    if (!ta.value) { ta.style.height = ''; return; }
    ta.style.height = 'auto';
    ta.style.height = Math.min(180, Math.max(34, ta.scrollHeight + 2)) + 'px';
}

function eventById(id) { return S.doc.events.find((e) => e.id === id) || null; }

function bindTable() {
    const box = $('tlmRows');
    box.addEventListener('input', (e) => {
        const el = e.target;
        const row = el.closest('.tlm-row'); if (!row) return;
        const ev = eventById(row.dataset.id); if (!ev) return;
        const f = el.dataset.f;
        if (f === 'desc') autoGrow(el);
        if (f === 'date' || f === 'title' || f === 'desc') {
            ev[f] = el.value;
            commit(f + ':' + ev.id);
            if (f === 'date') stage('first_date');
        }
    });
    box.addEventListener('change', (e) => {
        const el = e.target;
        if (el.dataset.f === 'date') {
            // the date the person left in the field: a refusal is reported once it is final, not per keystroke
            const row = el.closest('.tlm-row');
            const ev = row && eventById(row.dataset.id);
            const r = ev && String(ev.date || '').trim() ? parseDate(ev.date, { locale: LOCALE }) : null;
            if (r && !r.ok) TM.diag('date_' + (r.error || 'unknown'), { shape: TM.shapeOf(ev.date), locale: LOCALE });
            return;
        }
        if (el.dataset.f !== 'cat') return;
        const row = el.closest('.tlm-row'); const ev = row && eventById(row.dataset.id); if (!ev) return;
        ev.cat = el.value || null;
        commit('cat');
        ae('category_set');
    });
    box.addEventListener('focusin', (e) => {
        const row = e.target.closest('.tlm-row'); if (!row) return;
        select(row.dataset.id, { fromTable: true });
    });
    box.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || e.isComposing) return;
        const el = e.target;
        const f = el.dataset.f;
        if (!f || f === 'cat') return;
        if (f === 'desc' && e.shiftKey) return;
        e.preventDefault();
        const row = el.closest('.tlm-row');
        if (f === 'date') { const n = row.querySelector('[data-f=title]'); if (n) n.focus(); return; }
        // Enter on the title or description: the next row, a new one at the end
        const idx = S.doc.events.findIndex((x) => x.id === row.dataset.id);
        if (idx === S.doc.events.length - 1) addEvent({}, { focus: 'date' });
        else { const nx = rowEls.get(S.doc.events[idx + 1].id); const d = nx && nx.querySelector('[data-f=date]'); if (d) d.focus(); }
    });
    box.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-act]'); if (!b) return;
        const row = b.closest('.tlm-row'); const ev = row && eventById(row.dataset.id); if (!ev) return;
        if (b.dataset.act === 'del') deleteEvent(ev.id);
        else if (b.dataset.act === 'img') pickImageFor(ev.id);
    });
    // a multi-line paste into a date or title cell is a list, not one value
    box.addEventListener('paste', (e) => {
        const el = e.target;
        if (!el.dataset || (el.dataset.f !== 'date' && el.dataset.f !== 'title')) return;
        const text = (e.clipboardData && e.clipboardData.getData('text/plain')) || '';
        if (!/\n/.test(text.trim())) return;
        const r = parsePaste(text, LOCALE);
        if (!r.events.length) return;
        e.preventDefault();
        const row = el.closest('.tlm-row'); const ev = row && eventById(row.dataset.id);
        const blank = ev && !ev.date.trim() && !ev.title.trim() && !ev.desc.trim();
        importEvents(r, { replaceRow: blank ? ev.id : null });
    });
}

function updateFeedback() {
    const an = S.an;
    const byId = new Map(an.items.map((i) => [i.ev.id, i]));
    for (const ev of S.doc.events) {
        const row = rowEls.get(ev.id); if (!row) continue;
        const fb = row.querySelector('.tlm-fb');
        const inp = row.querySelector('[data-f=date]');
        const strip = row.querySelector('.tlm-row-strip');
        const th = theme(S.doc.view.theme);
        strip.style.background = colorOf(S.doc, ev, th);
        row.classList.toggle('sel', S.selected === ev.id);
        const err = an.errors[ev.id];
        if (err) {
            const code = err === 'range_order' ? (parseDate(ev.date, { locale: LOCALE }).bc ? 'err_range_order_bc' : 'err_range_order') : 'err_' + err;
            fb.textContent = I18N[code] != null ? t(code) : t('err_unknown');
            fb.className = 'tlm-fb bad';
            inp.setAttribute('aria-invalid', 'true');
            continue;
        }
        inp.removeAttribute('aria-invalid');
        const it = byId.get(ev.id);
        if (!it) { fb.textContent = ''; fb.className = 'tlm-fb'; continue; }
        fb.className = 'tlm-fb ok';
        fb.textContent = '✓ ' + feedbackText(it);
    }
}

function feedbackText(it) {
    const R = rules(LOCALE);
    const res = it.res;
    const date = iso(it.text);
    if (res.kind === 'ago') return fill(t('fb_ago'), { date });
    if (res.present) return fill(t('fb_present'), { date });
    if (res.b) {
        /* «1700–1721» lasted 21 years (the school answer: end − start); «XVIII век»
           is 1701–1800 and lasts 100 — a named century, decade or millennium is
           counted whole, from its first day to its last. */
        const n = res.unit ? Math.round(it.b - it.a) : res.b.y - res.a.y;
        // a phrase of the page's language with a number, set inside another sentence: isolated as well
        const years = iso(n >= 1 ? yearsText(n) : '< ' + yearsText(1));
        return fill(t('fb_period'), { date, years });
    }
    const c = centuryOf(res.a.y);
    const century = iso(R.century(c.n, c.bc));
    return fill(t('fb_point'), { date, century });
}

/* ── events: add, delete, select ─────────────────────────────────────────── */

function addEvent(fields, opts) {
    const ev = newEvent(fields || {});
    if (S.doc.events.length >= LIMITS.events) {
        // before 15.10.2026 «+ Event» simply did nothing on the 401st row
        toast(fill(t('events_limit'), { n: LIMITS.events }), 'warning');
        TM.diag('events_limit', { n: LIMITS.events });
        return null;
    }
    const at = opts && opts.after != null ? opts.after + 1 : S.doc.events.length;
    S.doc.events.splice(at, 0, ev);
    showEmpty(false);
    commit('add:' + ev.id, { rows: true });
    S.rowsDirty = true;
    render();
    if (opts && opts.focus) {
        const row = rowEls.get(ev.id);
        const inp = row && row.querySelector('[data-f=' + opts.focus + ']');
        if (inp) inp.focus();
    }
    select(ev.id, { fromTable: true });
    stage('first_event');
    return ev;
}

function deleteEvent(id) {
    const i = S.doc.events.findIndex((e) => e.id === id);
    if (i < 0) return;
    S.doc.events.splice(i, 1);
    if (S.selected === id) S.selected = null;
    commit('del', { rows: true });
    toast(t('deleted'), 'info');
    ae('event_delete');
}

function select(id, opts) {
    if (S.selected === id) return;
    S.selected = id;
    for (const [rid, row] of rowEls) row.classList.toggle('sel', rid === id);
    updateImgPanel();
    queueRender();
    void opts;
}

/* ── sheet interactions: select, drag, double-click, zoom, pan ───────────── */

function evFromTarget(tg) {
    const el = tg && tg.closest ? tg.closest('[data-ev]') : null;
    return el ? el.getAttribute('data-ev') : null;
}
function sheetPoint(e) {
    const host = $('tlmHost');
    const svg = host.querySelector('svg');
    const r = (svg || host).getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
}

const UNIT_YEARS = { day: 1 / 365.2425, month: 1 / 12, year: 1, decade: 10, century: 100 };

function shiftTD(td, unit, k) {
    const out = Object.assign({}, td);
    if (!k) return out;
    if (td.prec === 'day' && unit === 'day') {
        const x = new Date(Date.UTC(2000, td.m - 1, td.d)); x.setUTCFullYear(td.y);
        x.setUTCDate(x.getUTCDate() + k);
        out.y = x.getUTCFullYear(); out.m = x.getUTCMonth() + 1; out.d = x.getUTCDate();
        return out;
    }
    if ((td.prec === 'day' || td.prec === 'month') && (unit === 'day' || unit === 'month')) {
        const mm = td.m - 1 + k;
        out.y = td.y + Math.floor(mm / 12);
        out.m = ((mm % 12) + 12) % 12 + 1;
        if (td.prec === 'day') out.d = Math.min(td.d, daysInMonth(out.y, out.m));
        return out;
    }
    const years = unit === 'century' ? 100 * k : unit === 'decade' ? 10 * k : k;
    out.y = td.y + years;
    if (td.prec === 'day') out.d = Math.min(td.d, daysInMonth(out.y, out.m));
    return out;
}

/* A date the sheet writes into the table is the person's data: the bidi isolates of the printed form
   (an Arabic number range, «\u2066753–509\u2069 ق.م») stay out of the field, the CSV and the link. */
const plainDate = (s) => String(s).replace(/[\u2066-\u2069]/g, '');

/** The date string of a parsed date moved by k units — written the way this page's locale writes it. */
function movedDate(res, unit, k) {
    if (res.kind === 'ago') {
        const shift = k * UNIT_YEARS[unit];
        const a = Math.max(0, res.ago.a - shift);
        const nice = (v) => { if (v <= 0) return 0; const p = Math.pow(10, Math.floor(Math.log10(v)) - 2); return Math.round(v / p) * p; };
        const r2 = { ok: true, kind: 'ago', ago: { a: nice(a), b: res.ago.b == null ? null : nice(Math.max(0, res.ago.b - shift)) }, present: res.present, approx: res.approx };
        return formatDate(r2, LOCALE);
    }
    const coarse = { day: 0, month: 1, year: 2, decade: 3, century: 4 };
    const precUnit = res.a.prec === 'day' ? 'day' : res.a.prec === 'month' ? 'month' : 'year';
    const u = coarse[unit] < coarse[precUnit] ? precUnit : unit;
    const r2 = { ok: true, kind: 'cal', a: shiftTD(res.a, u, k), b: res.b ? shiftTD(res.b, u, k) : null, present: res.present, unit: null, approx: res.approx };
    return formatDate(r2, LOCALE, { showAD: false });
}

function dateAt(tv, unit) {
    const an = S.an;
    if (an && an.mode === 'ago') {
        const ago = Math.max(0, -tv);
        const p = ago > 0 ? Math.pow(10, Math.floor(Math.log10(ago)) - 1) : 1;
        return formatDate({ ok: true, kind: 'ago', ago: { a: Math.round(ago / p) * p, b: null }, present: false, approx: false }, LOCALE);
    }
    const d = dateOfT(tv);
    let td;
    if (unit === 'day') td = { y: d.y, m: d.m, d: d.d, prec: 'day' };
    else if (unit === 'month') td = { y: d.y, m: d.m, d: 0, prec: 'month' };
    else {
        let y = Math.round(tv);
        if (unit === 'decade') y = Math.round(tv / 10) * 10;
        if (unit === 'century') y = Math.round(tv / 100) * 100;
        if (y === 0) y = 1;
        td = { y, m: 0, d: 0, prec: 'year' };
    }
    return formatDate({ ok: true, kind: 'cal', a: td, b: null, present: false, unit: null, approx: false }, LOCALE, { showAD: false });
}

let drag = null;
let pan = null;
const pointers = new Map();
let pinch = null;

function bindSheet() {
    const view = $('tlmView');
    const tip = $('tlmTip');
    view.addEventListener('pointerdown', (e) => {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
            const [p1, p2] = Array.from(pointers.values());
            pinch = { d0: Math.hypot(p1.x - p2.x, p1.y - p2.y), z0: S.doc.view.zoom || 0 };
            drag = null; pan = null;
            return;
        }
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const id = evFromTarget(e.target);
        if (id) {
            const it = S.an.items.find((i) => i.ev.id === id);
            select(id);
            if (!it || S.doc.view.scale === 'uniform') return;
            const p = sheetPoint(e);
            drag = { id, it, x0: p.x, y0: p.y, t0: S.out.geo.toT(p.x, p.y), moved: false, pointer: e.pointerId, before: S.doc.events.find((x) => x.id === id).date };
            return;
        }
        if (e.pointerType === 'mouse') pan = { x: e.clientX, sl: view.scrollLeft, moved: false };
    });
    view.addEventListener('pointermove', (e) => {
        if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinch && pointers.size === 2) {
            const [p1, p2] = Array.from(pointers.values());
            const d = Math.hypot(p1.x - p2.x, p1.y - p2.y);
            if (pinch.d0 > 0) setZoom(pinch.z0 + Math.log(d / pinch.d0) / Math.log(1.25), { cx: (p1.x + p2.x) / 2 - view.getBoundingClientRect().left, live: true });
            return;
        }
        if (pan) {
            const dx = e.clientX - pan.x;
            if (Math.abs(dx) > 3) pan.moved = true;
            view.scrollLeft = pan.sl - dx;
            return;
        }
        if (!drag) return;
        const p = sheetPoint(e);
        if (!drag.moved && Math.hypot(p.x - drag.x0, p.y - drag.y0) < 5) return;
        if (!drag.moved) { drag.moved = true; try { view.setPointerCapture(drag.pointer); } catch (_) { /* old engines */ } $('tlmHost').classList.add('dragging'); }
        const tNow = S.out.geo.toT(p.x, p.y);
        const unit = snapUnit(S.out.geo.density(tNow));
        const k = Math.round((tNow - drag.t0) / UNIT_YEARS[unit]);
        const ev = S.doc.events.find((x) => x.id === drag.id);
        const next = k ? movedDate(drag.it.res, unit, k) : drag.before;
        if (ev && ev.date !== plainDate(next)) {
            ev.date = plainDate(next);
            const inp = rowEls.get(ev.id) && rowEls.get(ev.id).querySelector('[data-f=date]');
            if (inp) inp.value = ev.date;
            queueRender();
        }
        const vr = view.getBoundingClientRect();
        tip.hidden = false;
        const shown = iso(next);
        tip.textContent = fill(t('drag_to'), { date: shown });
        tip.style.left = (e.clientX - vr.left + view.scrollLeft) + 'px';
        tip.style.top = (e.clientY - vr.top) + 'px';
    });
    const end = (e) => {
        pointers.delete(e.pointerId);
        if (pinch && pointers.size < 2) { pinch = null; commit('zoom', { noRows: true }); }
        if (pan) { pan = null; }
        if (drag) {
            if (drag.moved) { commit('drag:' + drag.id); ae('drag_date', { unit: snapUnit(S.out.geo.density(drag.t0)) }); }
            drag = null;
            tip.hidden = true;
            $('tlmHost').classList.remove('dragging');
        }
    };
    view.addEventListener('pointerup', end);
    view.addEventListener('pointercancel', end);
    view.addEventListener('dblclick', (e) => {
        if (evFromTarget(e.target) || !S.out) return;
        const p = sheetPoint(e);
        const tv = S.out.geo.toT(p.x, p.y);
        if (!Number.isFinite(tv)) return;
        const unit = S.doc.view.scale === 'uniform' ? 'year' : snapUnit(S.out.geo.density(tv));
        const date = dateAt(tv, unit === 'day' || unit === 'month' ? unit : unit);
        const ev = addEvent({ date: plainDate(date) }, { focus: 'title' });
        const shown = iso(date);
        if (ev) { toast(fill(t('dbl_added'), { date: shown }), 'info'); funnel('dblclick_add', { unit }); }
    });
    view.addEventListener('wheel', (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const r = view.getBoundingClientRect();
        setZoom((S.doc.view.zoom || 0) + (e.deltaY < 0 ? 1 : -1) * Math.min(2, Math.max(0.35, Math.abs(e.deltaY) / 100)), { cx: e.clientX - r.left, live: true });
        clearTimeout(bindSheet._wt);
        bindSheet._wt = setTimeout(() => commit('zoom', { noRows: true }), 400);
    }, { passive: false });
    view.addEventListener('scroll', () => drawMiniViewport(), { passive: true });
    view.addEventListener('keydown', (e) => {
        if (e.key === '+' || e.key === '=') { e.preventDefault(); setZoom((S.doc.view.zoom || 0) + 1); commit('zoom', { noRows: true }); }
        if (e.key === '-') { e.preventDefault(); setZoom((S.doc.view.zoom || 0) - 1); commit('zoom', { noRows: true }); }
    });
}

function setZoom(z, o) {
    const view = $('tlmView');
    const zz = Math.max(-12, Math.min(40, Math.round(z * 100) / 100));
    if (zz === S.doc.view.zoom) return;
    const cx = o && o.cx != null ? o.cx : view.clientWidth / 2;
    let anchorT = null;
    if (S.out && S.doc.view.layout === 'horizontal') anchorT = S.out.geo.toT(view.scrollLeft + cx, S.out.geo.axis ? S.out.geo.axis.y : 0);
    S.doc.view.zoom = zz;
    render();
    if (anchorT != null && Number.isFinite(anchorT)) {
        const p = S.out.geo.toXY(anchorT);
        if (p) view.scrollLeft = Math.max(0, p.x - cx);
    }
    draftTouch();
}

/* ── minimap ─────────────────────────────────────────────────────────────── */

function drawMini() {
    const cv = $('tlmMini');
    const view = $('tlmView');
    const sc = S.out && S.out.scene;
    const need = sc && S.doc.view.layout === 'horizontal' && sc.w > view.clientWidth * 1.25 && !view.hidden;
    cv.hidden = !need;
    // the viewport frame lives beside the canvas: it must go with it, or it floats over the sheet
    if (cv.__ov) cv.__ov.hidden = !need;
    if (!need) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = cv.clientWidth || view.clientWidth, H = 46;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    const g = cv.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const th = theme(S.doc.view.theme);
    g.fillStyle = th.bg; g.fillRect(0, 0, W, H);
    const k = W / sc.w;
    const ay = H / 2;
    g.strokeStyle = th.axis; g.lineWidth = 2;
    g.beginPath(); g.moveTo(S.out.geo.axis.x0 * k, ay); g.lineTo(S.out.geo.axis.x1 * k, ay); g.stroke();
    let lane = 0;
    for (const it of S.an.items) {
        const x = S.out.geo.toXY(it.a).x * k;
        g.fillStyle = colorOf(S.doc, it.ev, th);
        if (it.period) {
            const x2 = S.out.geo.toXY(it.b).x * k;
            g.globalAlpha = 0.8; g.fillRect(x, ay + 5 + (lane++ % 3) * 5, Math.max(2, x2 - x), 4); g.globalAlpha = 1;
        } else { g.beginPath(); g.arc(x, ay - 7, 3, 0, Math.PI * 2); g.fill(); }
    }
    miniK = k;
    drawMiniViewport();
}
let miniK = 1;
function drawMiniViewport() {
    const cv = $('tlmMini');
    if (cv.hidden || !S.out) return;
    const view = $('tlmView');
    let ov = cv.__ov;
    if (!ov) {
        ov = document.createElement('div');
        ov.style.cssText = 'position:absolute;pointer-events:none;border:2px solid var(--lg-accent);border-radius:6px;background:color-mix(in srgb,var(--lg-accent) 12%,transparent)';
        cv.parentNode.style.position = cv.parentNode.style.position || 'relative';
        cv.parentNode.insertBefore(ov, cv.nextSibling);
        cv.__ov = ov;
    }
    ov.hidden = false;
    ov.style.left = (cv.offsetLeft + view.scrollLeft * miniK) + 'px';
    ov.style.top = cv.offsetTop + 'px';
    ov.style.width = Math.max(8, view.clientWidth * miniK) + 'px';
    ov.style.height = cv.offsetHeight + 'px';
}
function bindMini() {
    const cv = $('tlmMini');
    let down = false;
    const go = (e) => {
        const r = cv.getBoundingClientRect();
        const view = $('tlmView');
        view.scrollLeft = Math.max(0, (e.clientX - r.left) / miniK - view.clientWidth / 2);
    };
    cv.addEventListener('pointerdown', (e) => { down = true; try { cv.setPointerCapture(e.pointerId); } catch (_) { /* old engines */ } go(e); });
    cv.addEventListener('pointermove', (e) => { if (down) go(e); });
    cv.addEventListener('pointerup', () => { down = false; });
    cv.addEventListener('pointercancel', () => { down = false; });
}

/* ── controls ↔ document ─────────────────────────────────────────────────── */

function setSeg(boxId, attr, value) {
    for (const b of $(boxId).querySelectorAll('[' + attr + ']')) {
        const on = b.getAttribute(attr) === value;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    }
}
function syncControls() {
    const v = S.doc.view;
    $('tlmTitle').value = S.doc.title;
    $('tlmAuthor').value = S.doc.author;
    setSeg('tlmLayouts', 'data-layout', v.layout);
    setSeg('tlmScales', 'data-scale', v.scale);
    for (const b of $('tlmThemes').querySelectorAll('[data-theme-id]')) {
        const on = b.dataset.themeId === v.theme;
        b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false');
    }
    $('tlmFont').value = v.font;
    $('tlmMarker').value = v.marker;
    $('tlmShowDesc').checked = !!v.showDesc;
    $('tlmShowImages').checked = !!v.showImages;
    $('tlmShowLegend').checked = !!v.showLegend;
    $('tlmRtl').checked = v.dir === 'rtl';
    const hint = { linear: 'scale_hint_linear', uniform: 'scale_hint_uniform', broken: 'scale_hint_broken', log: 'scale_hint_log' }[v.scale];
    $('tlmScaleHint').textContent = t(hint);
    buildCats();
}
function updateScaleButtons() {
    const ago = S.an && S.an.mode === 'ago';
    const logB = $('tlmScales').querySelector('[data-scale=log]');
    logB.hidden = !ago;
    if (!ago && S.doc.view.scale === 'log') { S.doc.view.scale = 'linear'; setSeg('tlmScales', 'data-scale', 'linear'); }
}

function bindControls() {
    $('tlmLayouts').addEventListener('click', (e) => {
        const b = e.target.closest('[data-layout]'); if (!b) return;
        S.doc.view.layout = b.dataset.layout; S.doc.view.zoom = 0;
        setSeg('tlmLayouts', 'data-layout', b.dataset.layout);
        commit('layout', { noRows: true });
        funnel('layout', { v: b.dataset.layout });
    });
    $('tlmScales').addEventListener('click', (e) => {
        const b = e.target.closest('[data-scale]'); if (!b) return;
        S.doc.view.scale = b.dataset.scale; S.doc.view.zoom = 0;
        syncControls();
        commit('scale', { noRows: true });
        funnel('scale', { v: b.dataset.scale, mode: S.an ? S.an.mode : '' });
    });
    $('tlmThemes').addEventListener('click', (e) => {
        const b = e.target.closest('[data-theme-id]'); if (!b) return;
        S.doc.view.theme = b.dataset.themeId;
        syncControls(); ensureFonts();
        commit('theme', { noRows: true });
        S.rowsDirty = false;
        funnel('theme', { v: b.dataset.themeId });
    });
    $('tlmFont').addEventListener('change', (e) => { S.doc.view.font = e.target.value; ensureFonts(); commit('font', { noRows: true }); ae('font', { v: e.target.value }); });
    $('tlmMarker').addEventListener('change', (e) => { S.doc.view.marker = e.target.value; commit('marker', { noRows: true }); });
    for (const [id, key] of [['tlmShowDesc', 'showDesc'], ['tlmShowImages', 'showImages'], ['tlmShowLegend', 'showLegend']]) {
        $(id).addEventListener('change', (e) => { S.doc.view[key] = e.target.checked; commit(key, { noRows: true }); });
    }
    $('tlmRtl').addEventListener('change', (e) => { S.doc.view.dir = e.target.checked ? 'rtl' : 'ltr'; commit('dir', { noRows: true }); ae('rtl', { on: e.target.checked }); });
    $('tlmTitle').addEventListener('input', (e) => { S.doc.title = e.target.value; showEmpty(false); commit('doc_title'); });
    $('tlmAuthor').addEventListener('input', (e) => { S.doc.author = e.target.value; commit('doc_author'); });
    $('tlmZoomIn').addEventListener('click', () => { setZoom((S.doc.view.zoom || 0) + 1); commit('zoom', { noRows: true }); });
    $('tlmZoomOut').addEventListener('click', () => { setZoom((S.doc.view.zoom || 0) - 1); commit('zoom', { noRows: true }); });
    $('tlmZoomFit').addEventListener('click', () => { S.doc.view.zoom = 0; $('tlmView').scrollLeft = 0; commit('zoom', { noRows: true }); });
    $('tlmUndo').addEventListener('click', undo);
    $('tlmRedo').addEventListener('click', redo);
    $('tlmAddTop').addEventListener('click', () => { addEvent({}, { focus: 'date' }); ae('add_button'); });
    $('tlmAddRow').addEventListener('click', () => { addEvent({}, { focus: 'date' }); });
    $('tlmSort').addEventListener('click', sortByDate);
    document.addEventListener('keydown', (e) => {
        if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
        const k = e.key.toLowerCase();
        const inText = e.target && (e.target.tagName === 'TEXTAREA' || (e.target.tagName === 'INPUT' && e.target.type === 'text'));
        if (!$('tlmApp').contains(e.target) && e.target !== document.body) return;
        if (k === 'z' && !e.shiftKey && !inText) { e.preventDefault(); undo(); }
        else if ((k === 'y' || (k === 'z' && e.shiftKey)) && !inText) { e.preventDefault(); redo(); }
    });
    window.addEventListener('resize', () => { clearTimeout(bindControls._rt); bindControls._rt = setTimeout(queueRender, 150); });
}

function sortByDate() {
    const an = analyze(S.doc, { locale: LOCALE });
    const order = new Map(an.items.map((it, i) => [it.ev.id, i]));
    S.doc.events.sort((a, b) => (order.has(a.id) ? order.get(a.id) : 1e9) - (order.has(b.id) ? order.get(b.id) : 1e9));
    commit('sort', { rows: true });
    funnel('sort', { n: S.doc.events.length });
}

/* ── categories ──────────────────────────────────────────────────────────── */

function buildCats() {
    const box = $('tlmCats');
    box.textContent = '';
    const hidden = new Set(S.doc.view.hidden || []);
    for (const c of S.doc.cats) {
        const row = document.createElement('div');
        row.className = 'tlm-cat' + (hidden.has(c.id) ? ' off' : '');
        const col = document.createElement('input'); col.type = 'color'; col.value = c.color; col.setAttribute('aria-label', t('cat_color'));
        const name = document.createElement('input'); name.type = 'text'; name.className = 'tlm-in'; name.value = c.name; name.maxLength = LIMITS.catName; name.dir = 'auto';
        name.placeholder = t('cat_name_ph'); name.setAttribute('aria-label', t('cat_name_ph'));
        const eye = document.createElement('button'); eye.type = 'button'; eye.className = 'tlm-ib';
        const off = hidden.has(c.id);
        eye.setAttribute('aria-label', off ? t('cat_show') : t('cat_hide')); eye.title = eye.getAttribute('aria-label');
        eye.setAttribute('aria-pressed', off ? 'true' : 'false');
        eye.innerHTML = off
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M3 3l18 18M10.6 6.1A10 10 0 0 1 12 6c5 0 9 6 9 6a17 17 0 0 1-3 3.4M6.6 6.6A17 17 0 0 0 3 12s4 6 9 6a9 9 0 0 0 4.1-1"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 12s4-6 9-6 9 6 9 6-4 6-9 6-9-6-9-6z"/><circle cx="12" cy="12" r="2.5"/></svg>';
        const del = document.createElement('button'); del.type = 'button'; del.className = 'tlm-ib'; del.textContent = '✕';
        del.setAttribute('aria-label', t('cat_delete')); del.title = t('cat_delete');
        col.addEventListener('input', () => { c.color = col.value.toLowerCase(); commit('catcolor:' + c.id, { noRows: true }); });
        name.addEventListener('input', () => { c.name = name.value; commit('catname:' + c.id, { noRows: true }); refreshCatSelects(); });
        eye.addEventListener('click', () => {
            const h = new Set(S.doc.view.hidden || []);
            if (h.has(c.id)) h.delete(c.id); else h.add(c.id);
            S.doc.view.hidden = Array.from(h);
            buildCats();
            commit('filter', { noRows: true });
            ae('category_filter');
        });
        del.addEventListener('click', () => {
            S.doc.cats = S.doc.cats.filter((x) => x.id !== c.id);
            for (const e of S.doc.events) if (e.cat === c.id) e.cat = null;
            S.doc.view.hidden = (S.doc.view.hidden || []).filter((x) => x !== c.id);
            buildCats();
            commit('catdel', { rows: true });
        });
        row.append(col, name, eye, del);
        box.appendChild(row);
    }
}
function refreshCatSelects() {
    for (const row of rowEls.values()) {
        const sel = row.querySelector('[data-f=cat]');
        const ev = eventById(row.dataset.id);
        if (sel && ev) catOptions(sel, ev.cat);
    }
}
function addCategory() {
    if (S.doc.cats.length >= LIMITS.cats) {
        toast(fill(t('cats_limit'), { n: LIMITS.cats }), 'warning');
        TM.diag('cats_limit', { n: LIMITS.cats });
        return;
    }
    const pal = theme(S.doc.view.theme).palette;
    const used = new Set(S.doc.cats.map((c) => c.color));
    const color = pal.find((c) => !used.has(c)) || pal[S.doc.cats.length % pal.length];
    const catN = S.doc.cats.length + 1;
    const c = { id: newId('c'), name: fill(t('cat_new'), { n: catN }), color };
    S.doc.cats.push(c);
    buildCats();
    refreshCatSelects();
    commit('catadd', { noRows: true });
    const inputs = $('tlmCats').querySelectorAll('.tlm-in');
    const last = inputs[inputs.length - 1];
    if (last) { last.focus(); last.select(); }
    ae('category_add');
}

/* ── pictures panel ──────────────────────────────────────────────────────── */

function updateImgPanel() {
    const ev = S.selected ? eventById(S.selected) : null;
    const forTitle = ev ? iso(ev.title || ev.date || '—') : '';
    $('tlmImgFor').textContent = ev ? fill(t('img_for'), { title: forTitle }) : t('img_none');
    const row = $('tlmImgRow');
    if (ev && ev.img && S.urls.has(ev.img)) { row.hidden = false; $('tlmImgPrev').src = S.urls.get(ev.img); } else row.hidden = true;
}
function pickImageFor(id) {
    select(id, { fromTable: true });
    $('tlmImgBox').open = true;
    updateImgPanel();
    const input = document.querySelector('[data-uploader-for=tlmImgDrop]');
    if (input) input.click();
}
function bindImages() {
    const zone = $('tlmImgDrop');
    const onFile = async (file) => {
        if (!file) return;
        const ev = S.selected ? eventById(S.selected) : null;
        if (!ev) { toast(t('img_none'), 'warning'); return; }
        try {
            const blob = await shrinkImage(file);
            const id = newId('i');
            await addImageBlob(id, blob);
            ev.img = id;
            commit('img', { rows: true });
            updateImgPanel();
            ae('image_add', { kb: Math.round(blob.size / 1024) });
        } catch (_) {
            toast(t('img_failed'), 'error');
            TM.diag('img_failed', { type: String(file.type || '').slice(0, 20), kb: Math.round((file.size || 0) / 1024) });
        }
    };
    if (zone && typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, onFile);
    else if (zone) zone.addEventListener('tool-uploader:file', (e) => onFile(e.detail && e.detail.file));
    $('tlmImgRemove').addEventListener('click', () => {
        const ev = S.selected ? eventById(S.selected) : null;
        if (!ev) return;
        ev.img = null;
        commit('imgdel', { rows: true });
        updateImgPanel();
    });
}

/* ── import: paste a list, open CSV or a project ─────────────────────────── */

function importEvents(r, opts) {
    const catByName = new Map(S.doc.cats.map((c) => [c.name.trim().toLowerCase(), c.id]));
    const pal = theme(S.doc.view.theme).palette;
    const evs = [];
    for (const e of r.events) {
        let cat = null;
        const cn = String(e.cat || '').trim();
        if (cn) {
            const key = cn.toLowerCase();
            if (!catByName.has(key) && S.doc.cats.length < LIMITS.cats) {
                const c = { id: newId('c'), name: cn.slice(0, LIMITS.catName), color: pal[S.doc.cats.length % pal.length] };
                S.doc.cats.push(c); catByName.set(key, c.id);
            }
            cat = catByName.get(key) || null;
        }
        evs.push(newEvent({ date: String(e.date || '').slice(0, LIMITS.date), title: String(e.title || '').slice(0, LIMITS.evTitle), desc: String(e.desc || '').slice(0, LIMITS.desc), cat }));
    }
    if (opts && opts.replace) S.doc.events = [];
    if (opts && opts.replaceRow) {
        const i = S.doc.events.findIndex((x) => x.id === opts.replaceRow);
        if (i >= 0) S.doc.events.splice(i, 1, ...evs); else S.doc.events.push(...evs);
    } else S.doc.events.push(...evs);
    const over = Math.max(0, S.doc.events.length - LIMITS.events);
    S.doc.events = S.doc.events.slice(0, LIMITS.events);
    showEmpty(false);
    buildCats();
    commit('import', { rows: true });
    stage('first_event');
    funnel('import', { n: evs.length - over, format: r.format, errors: r.errors.length, replace: opts && opts.replace ? 1 : 0, cell: opts && opts.replaceRow ? 1 : 0 });
    if (r.errors.length) TM.diag('import_bad_lines', { lines: r.errors.length, found: evs.length, format: r.format });
    if (over) { toast(fill(t('events_limit'), { n: LIMITS.events }), 'warning'); TM.diag('events_limit', { n: LIMITS.events, over }); }
    return evs.length - over;
}
function importMessage(r) {
    const msg = $('tlmImportMsg');
    msg.hidden = false;
    if (!r.events.length) {
        msg.className = 'tlm-import-msg bad';
        msg.textContent = t('import_nothing');
        TM.diag('import_nothing', { lines: (r.errors || []).length, format: r.format });
        return;
    }
    msg.className = 'tlm-import-msg' + (r.errors.length ? ' bad' : '');
    const found = r.events.length;
    const lines = listOf(r.errors.map((x) => x.line).slice(0, 12), LOCALE);
    msg.textContent = fill(t('import_result'), { n: found }) + (r.errors.length ? ' ' + fill(t('import_bad_lines'), { lines }) : '');
}
function bindImport() {
    const go = (replace) => {
        const text = $('tlmImportText').value;
        const r = parsePaste(text, LOCALE);
        importMessage(r);
        if (!r.events.length) return;
        const added = importEvents(r, { replace });
        // the count of rows that went in (a list past the 400-event limit is cut, and said so)
        if (added === r.events.length) toast(fill(t('import_added'), { n: added }), 'success');
    };
    $('tlmImportAdd').addEventListener('click', () => go(false));
    $('tlmImportReplace').addEventListener('click', () => go(true));
    const openPaste = () => { $('tlmImportBox').open = true; showEmpty(false); if (!S.draftId && !S.doc.events.length) queueRender(); $('tlmImportText').focus(); };
    $('tlmPasteOpen').addEventListener('click', openPaste);
    $('tlmPasteOpen2').addEventListener('click', openPaste);
    const zone = $('tlmFileDrop');
    const onFile = async (file) => {
        if (!file) return;
        let text = '';
        try { text = await file.text(); } catch (_) { toast(t('import_file_err'), 'error'); return; }
        if (/\.json$/i.test(file.name || '') || /^\s*\{/.test(text)) {
            try {
                const { doc, images } = readProject(text, LOCALE);
                const st = store();
                if (st && S.draftId) st.release(S.draftId);
                S.draftId = st ? newDraftId() : null;
                if (st) st.claim(S.draftId);
                openDoc(doc, new Map(Object.entries(images)));
                draftTouch();
                const opened = iso(docLabel(doc) || t('draft_untitled'));
                toast(fill(t('import_opened'), { title: opened }), 'success');
                funnel('project_open', { n: doc.events.length });
            } catch (_) {
                toast(t('import_file_err'), 'error');
                TM.diag('import_file_err', { ext: String((file.name || '').split('.').pop()).slice(0, 8).toLowerCase(), kb: Math.round((file.size || 0) / 1024), json: 1 });
            }
            return;
        }
        const r = parsePaste(text, LOCALE);
        importMessage(r);
        if (!r.events.length) {
            toast(t('import_file_err'), 'error');
            TM.diag('import_file_err', { ext: String((file.name || '').split('.').pop()).slice(0, 8).toLowerCase(), kb: Math.round((file.size || 0) / 1024), json: 0 });
            return;
        }
        const added = importEvents(r, {});
        if (added === r.events.length) toast(fill(t('import_added'), { n: added }), 'success');
    };
    if (zone && typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, onFile);
    else if (zone) zone.addEventListener('tool-uploader:file', (e) => onFile(e.detail && e.detail.file));
}

/* ── years between two events ────────────────────────────────────────────── */

let betKey = '';
/* A closed <select> ignores isolate characters: there an Arabic number range keeps its
   order with LRM marks instead («\u200E753–509\u200E ق.م» — the form of the page's prose). */
const lrmRanges = (s) => String(s).replace(/[\u2066\u2069]/g, '\u200E');
function updateBetween() {
    const a = $('tlmBetA'), b = $('tlmBetB');
    const items = S.an.items;
    const key = items.map((i) => i.ev.id + i.text + i.ev.title).join('|');
    if (key !== betKey) {
        betKey = key;
        const va = a.value, vb = b.value;
        for (const sel of [a, b]) {
            sel.textContent = '';
            for (const it of items) {
                const o = document.createElement('option'); o.value = it.ev.id;
                o.textContent = lrmRanges(it.text) + ' — ' + (it.ev.title || '…');
                /* a closed <select> draws its option in the option's own direction and ignores
                   isolate characters (seen on an RTL page: «BC — Founding of Rome 753») */
                o.dir = textDir(o.textContent);
                sel.appendChild(o);
            }
        }
        if (items.length) {
            a.value = items.some((i) => i.ev.id === va) ? va : items[0].ev.id;
            b.value = items.some((i) => i.ev.id === vb) ? vb : items[items.length - 1].ev.id;
        }
    }
    for (const sel of [a, b]) { const o = sel.selectedOptions && sel.selectedOptions[0]; sel.dir = o ? o.dir : 'auto'; }
    betweenOut();
}
/* The working is a formula and reads left to right on every page («753 + 476 − 1
   = 1228»); the code builds it, so it is set apart as one left-to-right piece
   inside the translated sentence of a right-to-left page. */
function betweenOut() {
    const out = $('tlmBetOut');
    const A = S.an.items.find((i) => i.ev.id === $('tlmBetA').value);
    const B = S.an.items.find((i) => i.ev.id === $('tlmBetB').value);
    if (!A || !B || A === B) { out.textContent = t('between_pick'); return; }
    let [p, q] = A.a <= B.a ? [A, B] : [B, A];
    out.textContent = '';
    const big = document.createElement('b');
    const line = document.createElement('span');
    big.dir = 'auto'; line.dir = 'auto';
    if (p.res.kind === 'ago' || q.res.kind === 'ago') {
        const va = Math.round(-p.a), vb = Math.round(-q.a);
        const n = va - vb;
        big.textContent = yearsText(n);
        const formula = ltr(nf(va) + ' − ' + nf(vb) + ' = ' + nf(n));
        line.textContent = fill(t('between_deep'), { formula });
    } else {
        const ya = p.res.a.y, yb = q.res.a.y;
        const n = yb - ya;
        big.textContent = yearsText(n);
        const ha = hist(ya), hb = hist(yb);
        if (ha.bc && !hb.bc) {
            const formula = ltr(ha.n + ' + ' + hb.n + ' − 1 = ' + nf(n));
            line.textContent = fill(t('between_cross'), { a: ha.n, b: hb.n, formula });
        } else line.textContent = ltr(Math.max(ha.n, hb.n) + ' − ' + Math.min(ha.n, hb.n) + ' = ' + nf(n));
    }
    out.append(big, document.createElement('br'), line);
}

/* ── export ──────────────────────────────────────────────────────────────── */

function outName(ext) {
    const base = String(S.doc.title || '').trim() || t('print_title');
    try {
        if (window.__outputName) return window.__outputName.join(window.__outputName.sanitize(base), ext);
    } catch (_) { /* primitive absent — plain name below */ }
    return base.replace(/[\\/:*?"<>|]+/g, '').slice(0, 60) + '.' + ext;
}
function download(blob, ext) {
    const name = outName(ext);
    if (window.__downloadFile) return window.__downloadFile(blob, name);
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    return null;
}
/**
 * The paper of the print follows the sheet: a wide sheet on a landscape page, a tall one on a portrait
 * page — one @page rule added to the page's own stylesheet (CSSOM: no inline style) and taken away after
 * printing. → the function that removes it.
 */
function printPage() {
    const el = document.getElementById('tlmCss');
    const sheet = el && el.sheet;
    const sc = S.out && S.out.scene;
    if (!sheet || !sc) return () => {};
    const paper = $('tlmPaper').value === 'letter' ? 'letter' : ($('tlmPaper').value === 'a3' ? 'A3' : 'A4');
    const orient = $('tlmOrient').value === 'portrait' || ($('tlmOrient').value === 'auto' && sc.h > sc.w * 0.8) ? 'portrait' : 'landscape';
    let rule = null;
    try { rule = sheet.cssRules[sheet.insertRule('@page{size:' + paper + ' ' + orient + ';margin:10mm}', sheet.cssRules.length)]; } catch (_) { return () => {}; }
    return () => { for (let i = sheet.cssRules.length - 1; i >= 0; i--) if (sheet.cssRules[i] === rule) { try { sheet.deleteRule(i); } catch (_) { /* gone */ } } };
}
function canExport(via) {
    if (!S.an || !S.an.items.length) {
        toast(t('nothing_to_export'), 'warning');
        TM.diag('nothing_to_export', { via: via || '', rows: S.doc.events.length, errors: S.an ? Object.keys(S.an.errors || {}).length : 0 });
        return false;
    }
    return true;
}
const VIA = { tlmPng: 'png', tlmPdf: 'pdf', tlmPptx: 'pptx' };
async function busy(btn, fn) {
    if (btn.disabled) return;
    btn.disabled = true;
    const label = btn.textContent;
    btn.textContent = t('exporting');
    try { await fn(); } catch (e) {
        const via = VIA[btn.id] || btn.id;
        funnel('export_failed', { via });
        TM.caught(e, 'tlm_' + via, t('export_failed'));
    } finally { btn.disabled = false; btn.textContent = label; }
}
function exportScene() {
    // the sheet as it is, without the selection frame
    const sel = S.selected;
    if (!sel) return S.out.scene;
    const out = layout(S.doc, S.an, { measure, locale: LOCALE, width: viewWidth(), selected: null, imageSize: (id) => S.sizes.get(id) || null,
        labels: { approx: t('legend_approx'), gap: t('legend_gap') }, monthLabel: monthShort, dayLabel: dayShort });
    return out.scene;
}

async function exportPNG() {
    if (!canExport('png')) return;
    await busy($('tlmPng'), async () => {
        await ensureFonts();
        const want = +$('tlmDensity').value || 2;
        const scene = exportScene();
        const blob = await toPNG(scene, { density: want, family: FAM, images: S.bitmaps, transparent: $('tlmTransparent').checked });
        const k = nf1(blob.__density);
        if (blob.__density < want - 0.01) toast(fill(t('png_reduced'), { k }), 'info');
        window.__tlmLastExport = { kind: 'png', w: blob.__w, h: blob.__h, size: blob.size, density: blob.__density };
        download(blob, 'png');
        funnel('export', { via: 'png', density: want });
        complete('png', { w: blob.__w, h: blob.__h, density: Math.round(blob.__density * 100) / 100, transparent: $('tlmTransparent').checked ? 1 : 0 });
    });
}
async function exportSVG() {
    if (!canExport('svg')) return;
    const fc = faces(S.doc.view);
    const name = (id) => ({ noto: 'Noto Sans', playfair: 'Playfair Display', montserrat: 'Montserrat', comfortaa: 'Comfortaa', oswald: 'Oswald', caveat: 'Caveat' })[id] || 'Noto Sans';
    const images = {};
    for (const id of usedImageIds()) images[id] = await blobToDataURL(S.images.get(id));
    const svg = toSVG(exportScene(), {
        family: { title: `'${name(fc.title)}', Arial, sans-serif`, body: `'${name(fc.body)}', Arial, sans-serif` },
        title: S.doc.title || t('sheet_label'), imageUrl: (id) => images[id] || null, transparent: $('tlmTransparent').checked,
    });
    download(new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n' + svg], { type: 'image/svg+xml' }), 'svg');
    funnel('export', { via: 'svg' });
    complete('svg');
}
function blobToDataURL(b) {
    return new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => res(null); r.readAsDataURL(b); });
}
async function exportPDF() {
    if (!canExport('pdf')) return;
    await busy($('tlmPdf'), async () => {
        const { buildPDF } = await import('./pdf.js');
        await ensureFonts();
        const fc = faces(S.doc.view);
        const images = new Map();
        for (const id of usedImageIds()) images.set(id, { bytes: new Uint8Array(await S.images.get(id).arrayBuffer()), type: S.images.get(id).type });
        const blob = await buildPDF(exportScene(), {
            paper: $('tlmPaper').value, orient: $('tlmOrient').value, mode: $('tlmPdfMode').value,
            faces: fc, title: S.doc.title || t('print_title'), images,
            // the sheet labels are filled HERE, by the one fill() of the page — every «:n», in every locale
            T: { sheetOf: (i, n) => fill(t('pdf_sheet_of'), { i, n }), glueTo: (j) => fill(t('pdf_glue_to'), { j }) },
        });
        const plan = blob.__plan;
        window.__tlmLastExport = { kind: 'pdf', pages: plan.tiles.length, mode: plan.mode, size: blob.size, fontPt: plan.fontPt };
        download(blob, 'pdf');
        const sheets = plan.tiles.length, pt = nf1(plan.fontPt);
        if (plan.mode === 'tiles' && sheets > 1) toast(fill(t('pdf_done_tiles'), { n: sheets }), 'success');
        else if (!plan.legible) toast(fill(t('pdf_small'), { pt }), 'warning');
        funnel('export', { via: 'pdf', mode: plan.mode, paper: $('tlmPaper').value });
        complete('pdf', { pages: plan.tiles.length, pdf_mode: plan.mode, paper: $('tlmPaper').value, font_pt: Math.round(plan.fontPt * 10) / 10, legible: plan.legible ? 1 : 0 });
        if (!plan.legible) TM.diag('pdf_small', { font_pt: Math.round(plan.fontPt * 10) / 10, events: S.an.items.length, layout: S.doc.view.layout, paper: $('tlmPaper').value });
    });
}
async function exportPPTX() {
    if (!canExport('pptx')) return;
    await busy($('tlmPptx'), async () => {
        const { buildPPTX } = await import('./pptx.js');
        await ensureFonts();
        const hidden = new Set(S.doc.view.hidden || []);
        const items = S.an.items.filter((it) => !(it.ev.cat && hidden.has(it.ev.cat)));
        const urls = {};
        for (const id of usedImageIds()) urls[id] = await blobToDataURL(S.images.get(id));
        const th = theme(S.doc.view.theme);
        const blob = await buildPPTX({
            scene: exportScene(), doc: S.doc, items, theme: th, family: FAM, images: S.bitmaps, imageUrls: urls,
            title: S.doc.title || t('print_title'), colorOf: (it) => colorOf(S.doc, it.ev, th),
            T: { part: (i, n) => fill(t('pptx_part'), { i, n }), of: (i, n) => fill(t('present_of'), { i, n }), ends: (it) => (it.res && it.res.ok ? formatEnds(it.res, LOCALE, { showAD: !!S.an.hasBC }) : [it.text, it.text]) },
        });
        window.__tlmLastExport = { kind: 'pptx', size: blob.size, slides: items.length };
        download(blob, 'pptx');
        funnel('export', { via: 'pptx' });
        complete('pptx', { slides: items.length });
    });
}
/*
 * The link. ⚠️ Safari hands the clipboard (and the share sheet) only to the tap itself: a copy that
 * waited for the encoder was refused on every iPhone — «Could not copy», nothing else (before
 * 15.10.2026). So the link of the CURRENT timeline is made ahead, a moment after each change, and
 * the tap uses it at once; if it is not ready yet, the clipboard gets a promise (ClipboardItem), and
 * when even that is refused the link goes into a field under the buttons to copy by hand.
 */
let linkCache = null;      // { key, url, tooLong }
let linkTimer = 0;
const docKey = () => TM.hashOf(JSON.stringify(plain(S.doc)));
function makeLink() {
    const key = docKey();
    return encode(S.doc).then((payload) => {
        const url = new URL(location.href);
        url.search = '';
        url.hash = 't=' + payload;
        const str = url.toString();
        linkCache = { key, url: str, tooLong: str.length > SAFE_LINK_CHARS };
        return linkCache;
    });
}
function scheduleLink() {
    clearTimeout(linkTimer);
    linkTimer = setTimeout(() => { if (S.doc && S.an && S.an.items.length) makeLink().catch(() => { linkCache = null; }); }, 700);
}
function linkTooLong(len) {
    toast(t('link_too_long'), 'warning');
    TM.diag('link_too_long', { len, events: S.doc.events.length });
}
function linkCopied(url, via) {
    toast(usedImageIds().length ? t('link_no_images') : t('link_copied'), 'success');
    funnel('share', { via, len: url.length });
}
function showLinkField(url, why) {
    const out = $('tlmLinkOut');
    out.hidden = false;
    out.value = url;
    try { out.focus({ preventScroll: true }); out.select(); } catch (_) { /* read-only field */ }
    toast(t('copy_failed'), 'warning');
    funnel('share', { via: 'field', why, len: url.length });
}
function copyNow(url) {
    let p;
    try { p = window.__copyText ? window.__copyText(url) : navigator.clipboard.writeText(url).then(() => true, () => false); } catch (_) { p = Promise.resolve(false); }
    // __copyText never throws: it answers false when even the old way failed — no «copied» then
    Promise.resolve(p).then((ok) => (ok === false ? showLinkField(url, 'clipboard') : linkCopied(url, 'clipboard')), () => showLinkField(url, 'clipboard'));
}
function shareLink() {
    if (!canExport('link')) return;
    const ready = linkCache && linkCache.key === docKey() ? linkCache : null;
    if (ready) {
        if (ready.tooLong) { linkTooLong(ready.url.length); return; }
        if (navigator.share && window.matchMedia && matchMedia('(pointer:coarse)').matches) {
            navigator.share({ title: document.title, url: ready.url }).then(
                () => funnel('share', { via: 'native', len: ready.url.length }),
                (e) => { if (!(e && e.name === 'AbortError')) copyNow(ready.url); });
            return;
        }
        copyNow(ready.url);
        return;
    }
    const made = makeLink();
    let item = null;
    try {
        if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
            item = new ClipboardItem({ 'text/plain': made.then((r) => { if (r.tooLong) throw new Error('too_long'); return new Blob([r.url], { type: 'text/plain' }); }) });
        }
    } catch (_) { item = null; }
    if (item) {
        navigator.clipboard.write([item]).then(
            () => made.then((r) => linkCopied(r.url, 'clipboard')),
            () => made.then((r) => { if (r.tooLong) linkTooLong(r.url.length); else copyNow(r.url); }, () => toast(t('copy_failed'), 'error')));
        return;
    }
    made.then((r) => { if (r.tooLong) linkTooLong(r.url.length); else copyNow(r.url); }, () => toast(t('copy_failed'), 'error'));
}
async function saveProject() {
    const images = {};
    for (const id of usedImageIds()) images[id] = S.images.get(id);
    const json = await projectJSON(S.doc, images);
    download(new Blob([json], { type: 'application/json' }), 'json');
    const st = store();
    if (st && st.requestPersistence) st.requestPersistence();
    funnel('project_save', { n: S.doc.events.length, images: usedImageIds().length });
}
function exportCSV() {
    const csv = toCSV(S.doc, { date: t('csv_date'), title: t('csv_title'), desc: t('csv_desc'), cat: t('csv_cat') }, LOCALE);
    download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'csv');
    funnel('export', { via: 'csv' });
    complete('csv');
}
function bindExport() {
    $('tlmPng').addEventListener('click', exportPNG);
    $('tlmPdf').addEventListener('click', exportPDF);
    $('tlmPptx').addEventListener('click', exportPPTX);
    $('tlmSvg').addEventListener('click', exportSVG);
    $('tlmCsv').addEventListener('click', exportCSV);
    $('tlmShare').addEventListener('click', shareLink);
    $('tlmSaveFile').addEventListener('click', saveProject);
    $('tlmPrint').addEventListener('click', () => {
        if (!canExport('print')) return;
        funnel('export', { via: 'print' });
        complete('print');
        const unsize = printPage();
        const clean = () => { window.removeEventListener('afterprint', clean); unsize(); };
        window.addEventListener('afterprint', clean);
        window.print();
    });
    const bindSetting = () => {
        const TS = window.__toolSettings;
        if (!TS || !TS.bind) return;
        TS.bind(SLUG, $('tlmPaper'), 'paper', 'a4');
        TS.bind(SLUG, $('tlmOrient'), 'orient', 'auto');
        TS.bind(SLUG, $('tlmPdfMode'), 'pdfmode', 'auto');
        TS.bind(SLUG, $('tlmDensity'), 'density', '2');
        TS.bind(SLUG, $('tlmTransparent'), 'transparent', false);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindSetting); else bindSetting();
}

/* ── show mode: event after event, for the board ─────────────────────────── */

let showIdx = 0;
/* «Guess the date» (a teacher at the board): the event is shown without its date; the next press
   reveals the date, the press after that moves on. */
let quiz = false;
let revealed = false;
function showItems() {
    const hidden = new Set(S.doc.view.hidden || []);
    return S.an.items.filter((it) => !(it.ev.cat && hidden.has(it.ev.cat)));
}
function openShow() {
    if (!canExport('present')) return;
    showIdx = 0;
    revealed = false;
    $('tlmShow').hidden = false;
    $('tlmView').hidden = true;
    $('tlmMini').hidden = true;
    if ($('tlmMini').__ov) $('tlmMini').__ov.hidden = true;
    showRender();
    $('tlmShow').focus();
    funnel('present_open', { n: showItems().length });
}
function closeShow() {
    if ($('tlmShow').hidden) return;
    if (whole && whole.active()) whole.exit();
    else if (fullEl()) exitFull();
    $('tlmShow').hidden = true;
    $('tlmView').hidden = false;
    queueRender();
    $('tlmPresent').focus();
}
function showRender() {
    const list = showItems();
    if (!list.length) { closeShow(); return; }
    showIdx = Math.max(0, Math.min(list.length - 1, showIdx));
    const it = list[showIdx];
    const th = theme(S.doc.view.theme);
    const col = colorOf(S.doc, it.ev, th);
    const shownN = list.length, shownI = showIdx + 1;
    $('tlmShowOf').textContent = fill(t('present_of'), { i: shownI, n: shownN });
    const d = $('tlmShowDate'); d.textContent = quiz && !revealed ? '?' : it.text; d.style.color = col;
    $('tlmShowNext').textContent = quiz && !revealed ? t('present_reveal') : t('present_next') + ' →';
    $('tlmShowQuiz').setAttribute('aria-pressed', quiz ? 'true' : 'false');
    $('tlmShowTitle').textContent = it.ev.title || '';
    $('tlmShowDesc').textContent = it.ev.desc || '';
    const img = $('tlmShowImg');
    const url = it.ev.img && S.urls.get(it.ev.img);
    img.hidden = !url; if (url) img.src = url;
    $('tlmShowBody').classList.toggle('has-img', !!url);
    const track = $('tlmShowTrack');
    track.textContent = '';
    const sc = S.out && S.out.scale;
    const pos = (x) => (sc && sc.width ? Math.max(0, Math.min(1, sc.x(x) / sc.width)) : 0);
    /* the track runs the way the SHEET runs (its own «right to left» switch), not the
       page: the track is a physical strip (CSS direction:ltr), time placed by hand */
    const rtlSheet = S.doc.view.dir === 'rtl';
    list.forEach((o, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'tlm-show-dot' + (i === showIdx ? ' on' : '');
        // a dot of the track, not a button of its own: the site's 44 px touch floor made them overlapping 44 px
        // discs on a phone that covered «Previous» (QA 15.10.2026); «Previous / Next» are the touch targets
        b.setAttribute('data-tap-skip', '');
        const at = pos(o.a) * 100;
        b.style.left = (rtlSheet ? 100 - at : at) + '%';
        b.style.background = i === showIdx ? col : '';
        b.setAttribute('aria-label', iso(o.text) + ' — ' + iso(o.ev.title || ''));
        b.addEventListener('click', () => { showIdx = i; revealed = false; showRender(); });
        track.appendChild(b);
    });
    $('tlmShowPrev').disabled = showIdx === 0;
    $('tlmShowNext').disabled = showIdx === list.length - 1 && !(quiz && !revealed);
}
/** Forward: in «Guess the date» the first press reveals the date, the next one moves on. */
function showForward() {
    if (quiz && !revealed) { revealed = true; showRender(); ae('present_reveal'); return; }
    if (showIdx < showItems().length - 1) { showIdx++; revealed = false; showRender(); }
}
/* Full screen through the site's door `__wholeScreen`: real full screen where the browser gives it to an element,
   and the same show as an overlay over the window where it does not (iPhone Safari has no element full screen).
   The door is made on the first press, after the show's own keydown listener: on Esc in the overlay that
   listener sees the door still active and leaves closing the overlay to the door, instead of closing the show
   under it. Without the door (an old cached core) — the previous path, and no button where it cannot work. */
const fullEl = () => document.fullscreenElement || document.webkitFullscreenElement || null;
let whole = null;
function wholeDoor() {
    if (!whole && window.__wholeScreen) {
        whole = window.__wholeScreen($('tlmShow'), {
            key: 'tlm-show',
            onChange: (on) => { $('tlmShowFull').setAttribute('aria-pressed', on ? 'true' : 'false'); },
        });
    }
    return whole;
}
function exitFull() {
    if (window.__safeExitFullscreen) { window.__safeExitFullscreen(); return; }
    try { const p = document.exitFullscreen && document.exitFullscreen(); if (p && p.catch) p.catch(() => {}); } catch (_) { /* not in full screen */ }
}
function bindShow() {
    const show = $('tlmShow');
    if (!window.__wholeScreen && !(show.requestFullscreen || show.webkitRequestFullscreen)) $('tlmShowFull').hidden = true;
    $('tlmPresent').addEventListener('click', openShow);
    $('tlmShowClose').addEventListener('click', closeShow);
    $('tlmShowPrev').addEventListener('click', () => { showIdx--; revealed = false; showRender(); });
    $('tlmShowNext').addEventListener('click', showForward);
    $('tlmShowQuiz').addEventListener('click', () => {
        quiz = !quiz;
        revealed = false;
        showRender();
        funnel('present_quiz', { on: quiz ? 1 : 0, n: showItems().length });
    });
    $('tlmShowFull').addEventListener('click', () => {
        const el = $('tlmShow');
        const door = wholeDoor();
        if (door) { door.toggle(); return; }
        if (fullEl()) exitFull();
        else if (window.__safeFullscreen) window.__safeFullscreen(el);
        else if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    });
    document.addEventListener('keydown', (e) => {
        if ($('tlmShow').hidden) return;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName))) return;
        const n = showItems().length;
        if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); showForward(); }
        else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); if (showIdx > 0) { showIdx--; revealed = false; showRender(); } }
        else if (e.key === 'Home') { e.preventDefault(); showIdx = 0; revealed = false; showRender(); }
        else if (e.key === 'End') { e.preventDefault(); showIdx = n - 1; revealed = false; showRender(); }
        else if (e.key === 'Escape') { e.preventDefault(); if (whole && whole.active() === 'overlay') return; closeShow(); }
    });
}

/* ── empty state ─────────────────────────────────────────────────────────── */

function showEmpty(on) {
    $('tlmEmpty').hidden = !on;
    $('tlmView').hidden = on || !$('tlmShow').hidden;
    $('tlmTools').hidden = false;
}
function bindEmpty() {
    $('tlmTpls').addEventListener('click', (e) => {
        const b = e.target.closest('[data-tpl]'); if (!b) return;
        const d = templateDoc(b.dataset.tpl); if (!d) return;
        const st = store();
        if (st && S.draftId) st.release(S.draftId);
        S.draftId = st ? newDraftId() : null;
        if (st) st.claim(S.draftId);
        openDoc(d, null);
        draftTouch();
        const tplTitle = iso(d.title);
        toast(fill(t('tpl_opened'), { title: tplTitle }), 'success');
        stage('template', { key: b.dataset.tpl });
        funnel('template', { key: b.dataset.tpl });
    });
    $('tlmBlank').addEventListener('click', () => newTimeline());
    $('tlmNew').addEventListener('click', () => newTimeline());
    $('tlmCatAdd').addEventListener('click', addCategory);
    for (const id of ['tlmBetA', 'tlmBetB']) {
        $(id).addEventListener('change', (e) => { const o = e.target.selectedOptions[0]; e.target.dir = o ? o.dir : 'auto'; betweenOut(); });
    }
}
function newTimeline() {
    const st = store();
    if (st && S.draftId) st.release(S.draftId);
    S.draftId = null;
    openDoc(narrowStart(emptyDoc(LOCALE)), null);
    addEvent({}, { focus: 'date' });
    funnel('new_timeline', {});
    refreshDraftList();
}

/* ── boot ────────────────────────────────────────────────────────────────── */

async function boot() {
    S.doc = emptyDoc(LOCALE);
    S.hist.reset(S.doc);
    bindTable(); bindSheet(); bindMini(); bindControls(); bindImages(); bindImport(); bindExport(); bindShow(); bindEmpty();
    syncControls();
    ensureFonts();

    // 1) a shared link opens as the visitor's own copy
    const payload = payloadFromHash(location.hash);
    if (payload) {
        try {
            const doc = await decode(payload, LOCALE);
            const st = store();
            S.draftId = st ? newDraftId() : null;
            if (st) st.claim(S.draftId);
            openDoc(doc, null);
            draftTouch();
            toast(t('link_opened'), 'success');
            funnel('link_open', { n: doc.events.length });
            try { history.replaceState(null, '', location.pathname + location.search); } catch (_) { /* old engines */ }
            afterBoot();
            return;
        } catch (_) {
            toast(t('link_broken'), 'error');
            TM.diag('link_broken', { len: payload.length });
        }
    }
    // 2) the timeline that was open last time
    const st = store();
    if (st) {
        try {
            const lost = await st.takeLost();
            if (lost && lost.length) {
                const titles = listOf(lost.map((x) => quoted(x.label || t('draft_untitled'))), LOCALE);
                toast(fill(t('drafts_lost'), { titles }), 'warning');
            }
            const rows = await st.list();
            const busyTabs = await st.claimedElsewhere();
            const last = rows.find((r) => !busyTabs[r.id]);
            if (last && await loadDraft(last.id)) { afterBoot(); return; }
        } catch (_) { /* no storage: start fresh */ }
    }
    // 3) the empty state with examples
    showEmpty(true);
    queueRender();
    afterBoot();
}
function afterBoot() {
    refreshDraftList();
    updateScaleButtons();
    stage('ready', { locale: LOCALE, events: S.doc.events.length });
    window.__tlmReady = true;
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

export { S as state, render, parseDate, tOf, isBC, fitDensity, THEMES };
